import React, { useState, useEffect, useCallback } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  EyeIcon, 
  TrashIcon,
  ScaleIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { Package } from 'lucide-react';
import { AddPackagingModal, EditPackagingModal, PackagingDetailsModal, DeletePackagingModal } from '../modals';
import weighingService from '../../services/weighing';
import { Conditionnement } from '../../types/weighing';

const PackagingManagement: React.FC = () => {
  const [packagings, setPackagings] = useState<Conditionnement[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 10;

  // États pour les modales
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPackaging, setSelectedPackaging] = useState<Conditionnement | null>(null);

  useEffect(() => {
    loadPackagings();
  }, [currentPage, searchTerm, filterType]);

  const loadPackagings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        ordering: 'nom',
        search: searchTerm || undefined,
        type: filterType || undefined,
        _t: Date.now() // Cache busting
      };

      const response = await weighingService.getConditionnements(params);
      if (response && response.results) {
        setPackagings(response.results || []);
        setTotalCount(response.count || 0);
        setTotalPages(Math.ceil((response.count || 0) / pageSize));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des conditionnements:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterType, pageSize]);

  const handleAddPackaging = async (packagingData: any) => {
    try {
      const response = await weighingService.createConditionnement(packagingData);
      if (response.success) {
        await loadPackagings();
        setIsAddModalOpen(false);
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du conditionnement:', error);
    }
  };

  const handleEditPackaging = async (packagingData: any) => {
    if (!selectedPackaging) return;
    try {
      console.log('Données envoyées:', packagingData);
      console.log('Types des données:', {
        nom: typeof packagingData.nom,
        capacite: typeof packagingData.capacite,
        poids_tare: typeof packagingData.poids_tare,
        dimensions: typeof packagingData.dimensions
      });
      
      const response = await weighingService.updateConditionnement(selectedPackaging.id, packagingData);
      if (response.success) {
        await loadPackagings();
        setIsEditModalOpen(false);
        setSelectedPackaging(null);
      }
    } catch (error) {
      console.error('Erreur lors de la modification du conditionnement:', error);
    }
  };

  const handleDeletePackaging = async () => {
    if (!selectedPackaging) return;
    try {
      const response = await weighingService.deleteConditionnement(selectedPackaging.id);
      if (response.success) {
        await loadPackagings();
        setIsDeleteModalOpen(false);
        setSelectedPackaging(null);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du conditionnement:', error);
    }
  };

  const openEditModal = (packaging: Conditionnement) => {
    setSelectedPackaging(packaging);
    setIsEditModalOpen(true);
  };

  const openDetailsModal = (packaging: Conditionnement) => {
    setSelectedPackaging(packaging);
    setIsDetailsModalOpen(true);
  };

  const openDeleteModal = (packaging: Conditionnement) => {
    setSelectedPackaging(packaging);
    setIsDeleteModalOpen(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (value: string) => {
    setFilterType(value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Statistiques
  const totalPackagings = totalCount;
  const totalCapacity = packagings.reduce((sum, p) => sum + (parseFloat(p.capacite?.toString() || '0') || 0), 0);
  const averageTare = packagings.length > 0 ? packagings.reduce((sum, p) => sum + (parseFloat(p.poids_tare?.toString() || '0') || 0), 0) / packagings.length : 0;

  // Ajoutez cette fonction de conversion de type
  const convertToPackaging = (conditionnement: Conditionnement) => ({
    ...conditionnement,
    type: conditionnement.type || '',
    capacite: conditionnement.capacite || 0,
    unite: conditionnement.unite || '',
    dimensions: conditionnement.dimensions || '',
    poids_tare: conditionnement.poids_tare || 0,
    statut: conditionnement.statut || 'ACTIF',
    created_at: conditionnement.created_at || new Date().toISOString(),
    updated_at: conditionnement.updated_at || new Date().toISOString(),
    volume_calcule: conditionnement.volume_calcule || 0
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        <span className="ml-3 text-gray-600">Chargement des conditionnements...</span>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion du Conditionnement</h1>
          <p className="text-gray-600">Gérez vos types de conditionnement et emballages</p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Ajouter un conditionnement
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Package className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Conditionnements</p>
              <p className="text-2xl font-bold text-gray-900">{totalPackagings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ScaleIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Capacité Totale</p>
              <p className="text-2xl font-bold text-gray-900">{totalCapacity} kg</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <TruckIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tare Moyenne</p>
              <p className="text-2xl font-bold text-gray-900">{averageTare.toFixed(2)} kg</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Rechercher un conditionnement..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <div className="w-full md:w-48">
            <select
              value={filterType}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">Tous les types</option>
              <option value="sac">Sac</option>
              <option value="carton">Carton</option>
              <option value="bidon">Bidon</option>
              <option value="palette">Palette</option>
              <option value="autre">Autre</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tableau des conditionnements */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conditionnement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capacité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dimensions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tare
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {packagings.map((packaging) => (
                <tr key={packaging.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{packaging.nom}</div>
                      <div className="text-sm text-gray-500">ID: {packaging.id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {packaging.type_display || packaging.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {packaging.capacite ? `${parseFloat(packaging.capacite.toString()).toFixed(2)} ${packaging.unite_display || packaging.unite}` : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {packaging.dimensions || 'Non défini'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {packaging.poids_tare ? `${parseFloat(packaging.poids_tare.toString()).toFixed(2)} kg` : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      packaging.statut === 'actif' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {packaging.statut_display || (packaging.statut === 'actif' ? 'Actif' : 'Inactif')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openDetailsModal(packaging)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Voir les détails"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(packaging)}
                        className="text-orange-600 hover:text-orange-900 p-1"
                        title="Modifier"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(packaging)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Supprimer"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {packagings.length === 0 && !loading && (
          <div className="text-center py-8">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun conditionnement trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterType ? 'Essayez de modifier vos filtres.' : 'Commencez par ajouter votre premier conditionnement.'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> à{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, totalCount)}
                </span>{' '}
                sur <span className="font-medium">{totalCount}</span> résultats
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Précédent</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === currentPage
                          ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Suivant</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modales */}
      <AddPackagingModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddPackaging}
      />

      {selectedPackaging && (
        <>
          <EditPackagingModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedPackaging(null);
            }}
            onSave={handleEditPackaging}
            packaging={convertToPackaging(selectedPackaging!)}
          />

          <PackagingDetailsModal
            isOpen={isDetailsModalOpen}
            onClose={() => {
              setIsDetailsModalOpen(false);
              setSelectedPackaging(null);
            }}
            packaging={convertToPackaging(selectedPackaging!)}
          />

          <DeletePackagingModal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedPackaging(null);
            }}
            onConfirm={handleDeletePackaging}
            packaging={convertToPackaging(selectedPackaging!)}
          />
        </>
      )}
    </div>
  );
};

export default PackagingManagement;
